import { toasts } from "@/src/components/toasts/toasts";
import {
  AllDataRoomFoldersDocument,
  AllDataRoomFoldersQuery,
  DealDocument,
  DealQuery,
} from "@/src/graphql/generated";
import useGqlClient from "@/src/hooks/useGqlClient";
import { buildFolderHierarchy } from "@/src/utils/buildFolderHierarchy";
import { format } from "date-fns";
import { saveAs } from "file-saver";
import { useCallback, useState } from "react";
import { DataRoomFilters } from "..";
import { DataRoomIndexPdfDocument } from "./DataRoomIndexPdfDocument";
import { flattenFolderHierarchy } from "./flattenFolderHierarchy";

function filterFoldersForExport(
  folders: AllDataRoomFoldersQuery["deal"]["dataRoom"]["allFolders"],
  filters: DataRoomFilters,
) {
  return folders.filter((folder) => {
    if (folder.parentFolders.length === 0) {
      return true;
    }

    if (filters.createdBy && folder.createdBy.id !== filters.createdBy.id) {
      return false;
    }

    if (filters.lastUpdated && folder.updatedAt < filters.lastUpdated) {
      return false;
    }

    return true;
  });
}

function sanitizeFilename(name: string) {
  return name.replace(/[/\\?%*:|"<>]/g, "-").trim();
}

export function useDataRoomIndexPdf(dealId: string) {
  const client = useGqlClient();
  const [isLoading, setIsLoading] = useState(false);

  const download = useCallback(
    async (filters: DataRoomFilters, indexingEnabled: boolean) => {
      setIsLoading(true);
      toasts.info("Generating index PDF...");

      try {
        const [foldersData, dealData] = await Promise.all([
          client.request<AllDataRoomFoldersQuery>({
            document: AllDataRoomFoldersDocument,
            variables: { dealId },
          }),
          client.request<DealQuery>({
            document: DealDocument,
            variables: { id: dealId },
          }),
        ]);

        const filteredFolders = filterFoldersForExport(
          foldersData.deal.dataRoom.allFolders,
          filters,
        );

        const folderHierarchy = buildFolderHierarchy(
          filteredFolders,
          indexingEnabled,
          [],
          [],
          filters,
        );

        const rows = flattenFolderHierarchy(
          folderHierarchy as Parameters<typeof flattenFolderHierarchy>[0],
          dealId,
          indexingEnabled,
          window.location.origin,
        );

        if (rows.length === 0) {
          toasts.error("No items to export");
          return;
        }

        const dealName = dealData.deal.company.name;
        const meta = {
          dealName,
          exportedAt: format(new Date(), "MMMM d, yyyy"),
          indexingEnabled,
        };

        const { pdf } = await import("@react-pdf/renderer");
        const blob = await pdf(
          DataRoomIndexPdfDocument({ rows, meta }),
        ).toBlob();

        saveAs(blob, `${sanitizeFilename(dealName)} - Data Room Index.pdf`);
        toasts.success("Index PDF downloaded");
      } catch {
        toasts.error("Failed to generate index PDF");
      } finally {
        setIsLoading(false);
      }
    },
    [client, dealId],
  );

  return { download, isLoading };
}
